<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import aiPendingActionsAPI from 'dashboard/api/nexceo/aiPendingActions';
import nexceoProjectOptionsAPI from 'dashboard/api/nexceo/projectOptions';

const store = useStore();
const router = useRouter();
const { accountId } = useAccount();

// ── Auth ─────────────────────────────────────────────────────────────────────
const currentUser = computed(() => store.getters['auth/getCurrentUser']);
const isAdmin = computed(() => currentUser.value?.role === 'administrator');

// ── State ─────────────────────────────────────────────────────────────────────
const actions = computed(
  () => store.getters['nexceoAiPendingActions/getGlobalActions']
);
const isLoading = ref(false);
const loadError = ref('');
const stats = ref({
  pending: 0,
  approved_today: 0,
  rejected_today: 0,
  expired: 0,
});
const statsLoading = ref(false);

const projects = ref([]);

// ── Filters ───────────────────────────────────────────────────────────────────
const activeTab = ref('pending');
const filterProject = ref('');
const filterActionType = ref('');
const searchQuery = ref('');

const TABS = [
  { key: 'pending', label: 'Pending', icon: 'i-lucide-clock' },
  { key: 'approved', label: 'Approved', icon: 'i-lucide-check-circle' },
  { key: 'rejected', label: 'Rejected', icon: 'i-lucide-x-circle' },
  { key: 'expired', label: 'Expired', icon: 'i-lucide-timer-off' },
  { key: '', label: 'All', icon: 'i-lucide-list' },
];

const ACTION_TYPE_OPTIONS = [
  {
    value: 'share_document_requires_approval',
    label: 'Document (Approval Required)',
  },
  { value: 'share_price_sheet', label: 'Price Sheet' },
  { value: 'share_payment_plan', label: 'Payment Plan' },
  { value: 'share_legal', label: 'Legal Document' },
  { value: 'share_rera', label: 'RERA Document' },
  { value: 'share_inventory', label: 'Inventory' },
  { value: 'send_offer_message', label: 'Offer Message' },
  { value: 'block_unit', label: 'Block Unit' },
  { value: 'reserve_unit', label: 'Reserve Unit' },
];

const ACTION_TYPE_LABELS = Object.fromEntries(
  ACTION_TYPE_OPTIONS.map(o => [o.value, o.label])
);

function actionLabel(type) {
  return ACTION_TYPE_LABELS[type] || type?.replace(/_/g, ' ');
}

// ── Filtered list ─────────────────────────────────────────────────────────────
const filteredActions = computed(() => {
  let list = actions.value;
  if (filterProject.value)
    list = list.filter(
      a => String(a.project_id) === String(filterProject.value)
    );
  if (filterActionType.value)
    list = list.filter(a => a.action_type === filterActionType.value);
  if (searchQuery.value.trim()) {
    const q = searchQuery.value.trim().toLowerCase();
    list = list.filter(
      a =>
        (a.lead?.name || '').toLowerCase().includes(q) ||
        (a.lead?.phone_number || '').includes(q) ||
        (a.lead?.email || '').toLowerCase().includes(q) ||
        (a.project?.name || '').toLowerCase().includes(q)
    );
  }
  return list;
});

// ── Load ──────────────────────────────────────────────────────────────────────
async function load() {
  isLoading.value = true;
  loadError.value = '';
  try {
    const filters = {};
    if (activeTab.value) filters.status = activeTab.value;
    if (filterProject.value) filters.project_id = filterProject.value;
    if (filterActionType.value) filters.action_type = filterActionType.value;
    await store.dispatch('nexceoAiPendingActions/fetchGlobal', {
      accountId: accountId.value,
      filters,
    });
  } catch {
    loadError.value = 'Could not load AI approvals.';
  } finally {
    isLoading.value = false;
  }
}

async function loadStats() {
  statsLoading.value = true;
  try {
    const { data } = await aiPendingActionsAPI.stats(accountId.value);
    stats.value = data;
  } catch {
    // non-critical
  } finally {
    statsLoading.value = false;
  }
}

async function refresh() {
  await Promise.all([load(), loadStats()]);
}

async function switchTab(key) {
  activeTab.value = key;
  await load();
}

onMounted(async () => {
  const [projectsRes] = await Promise.all([
    nexceoProjectOptionsAPI.get(),
    refresh(),
  ]);
  projects.value = projectsRes.data?.projects ?? [];
});

// ── Approve ────────────────────────────────────────────────────────────────────
const confirmingId = ref(null); // card pending inline confirm

function startApprove(action) {
  confirmingId.value = action.id;
}
function cancelApprove() {
  confirmingId.value = null;
}

async function confirmApprove(action) {
  confirmingId.value = null;
  try {
    await store.dispatch('nexceoAiPendingActions/approve', {
      accountId: accountId.value,
      actionId: action.id,
    });
    useAlert('Action approved and will be executed.');
    await refresh();
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Approval failed.');
  }
}

// ── Reject ─────────────────────────────────────────────────────────────────────
const rejectingAction = ref(null);
const rejectReason = ref('');

function startReject(action) {
  rejectingAction.value = action;
  rejectReason.value = '';
  confirmingId.value = null;
}
function cancelReject() {
  rejectingAction.value = null;
  rejectReason.value = '';
}

async function confirmReject() {
  if (!rejectingAction.value) return;
  const action = rejectingAction.value;
  rejectingAction.value = null;
  try {
    await store.dispatch('nexceoAiPendingActions/reject', {
      accountId: accountId.value,
      actionId: action.id,
      reason: rejectReason.value,
    });
    useAlert('Action rejected.');
    await refresh();
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Rejection failed.');
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────────
const STATUS_META = {
  pending: { cls: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  approved: { cls: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  rejected: { cls: 'bg-red-100 text-red-600', dot: 'bg-red-500' },
  expired: { cls: 'bg-gray-100 text-gray-500', dot: 'bg-gray-400' },
  executed: { cls: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500' },
  failed: { cls: 'bg-red-100 text-red-700', dot: 'bg-red-600' },
};

function statusMeta(s) {
  return STATUS_META[s] || STATUS_META.expired;
}

function fmtDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function timeAgo(dt) {
  if (!dt) return '';
  const sec = Math.floor((Date.now() - new Date(dt)) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function conversationUrl(action) {
  if (!action.conversation_id) return null;
  return `/app/accounts/${accountId.value}/conversations/${action.conversation_id}`;
}

function openLeadEdit(action) {
  if (!action.lead_id) return;
  router.push({
    name: 'nexceo_leads_edit',
    params: { accountId: accountId.value, id: action.lead_id },
    query: { return_to: 'ai_approvals' },
  });
}

function openSettings() {
  router.push({
    name: 'nexceo_settings',
    params: { accountId: accountId.value },
  });
}

const hasActiveFilters = computed(
  () =>
    !!(
      filterProject.value ||
      filterActionType.value ||
      searchQuery.value.trim()
    )
);

function clearFilters() {
  filterProject.value = '';
  filterActionType.value = '';
  searchQuery.value = '';
  load();
}
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- ── Header ──────────────────────────────────────────────────────────── -->
    <div
      class="flex items-center gap-3 px-4 h-14 bg-n-background border-b border-n-weak shrink-0"
    >
      <span class="i-lucide-shield-check size-4 text-woot-500 shrink-0" />
      <div class="flex-1 min-w-0">
        <h1 class="text-base font-semibold text-n-slate-12 leading-tight">
          AI Approval Queue
        </h1>
        <p class="text-xs text-n-slate-7 leading-tight hidden sm:block">
          Review sensitive AI actions before they are sent to customers
        </p>
      </div>

      <span
        v-if="stats.pending > 0"
        class="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-amber-500 text-white tabular-nums shrink-0"
      >
        {{ stats.pending }}
      </span>

      <!-- Refresh -->
      <button
        :disabled="isLoading"
        class="flex items-center justify-center w-8 h-8 rounded-lg border border-n-weak text-n-slate-8 hover:border-woot-400 hover:text-woot-500 disabled:opacity-50 transition-colors"
        title="Refresh"
        @click="refresh"
      >
        <span
          class="size-3.5"
          :class="
            isLoading ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-refresh-cw'
          "
        />
      </button>
    </div>

    <!-- ── Summary cards ────────────────────────────────────────────────────── -->
    <div
      class="grid grid-cols-2 sm:grid-cols-4 gap-3 px-4 py-3 border-b border-n-weak shrink-0"
    >
      <div
        class="flex items-center gap-3 p-3 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <div
          class="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0"
        >
          <span class="i-lucide-clock size-4 text-amber-600" />
        </div>
        <div>
          <p class="text-xs text-n-slate-8">Pending</p>
          <p
            class="text-lg font-bold text-n-slate-12 leading-tight tabular-nums"
          >
            {{ statsLoading ? '…' : stats.pending }}
          </p>
        </div>
      </div>

      <div
        class="flex items-center gap-3 p-3 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <div
          class="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center shrink-0"
        >
          <span class="i-lucide-check-circle size-4 text-green-600" />
        </div>
        <div>
          <p class="text-xs text-n-slate-8">Approved Today</p>
          <p
            class="text-lg font-bold text-n-slate-12 leading-tight tabular-nums"
          >
            {{ statsLoading ? '…' : stats.approved_today }}
          </p>
        </div>
      </div>

      <div
        class="flex items-center gap-3 p-3 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <div
          class="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center shrink-0"
        >
          <span class="i-lucide-x-circle size-4 text-red-500" />
        </div>
        <div>
          <p class="text-xs text-n-slate-8">Rejected Today</p>
          <p
            class="text-lg font-bold text-n-slate-12 leading-tight tabular-nums"
          >
            {{ statsLoading ? '…' : stats.rejected_today }}
          </p>
        </div>
      </div>

      <div
        class="flex items-center gap-3 p-3 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <div
          class="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center shrink-0"
        >
          <span class="i-lucide-timer-off size-4 text-gray-500" />
        </div>
        <div>
          <p class="text-xs text-n-slate-8">Expired</p>
          <p
            class="text-lg font-bold text-n-slate-12 leading-tight tabular-nums"
          >
            {{ statsLoading ? '…' : stats.expired }}
          </p>
        </div>
      </div>
    </div>

    <!-- ── Tabs ──────────────────────────────────────────────────────────────── -->
    <div
      class="flex items-center gap-1 px-4 py-2 border-b border-n-weak shrink-0 overflow-x-auto"
    >
      <button
        v-for="tab in TABS"
        :key="tab.key"
        class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
        :class="
          activeTab === tab.key
            ? 'bg-woot-500 text-white'
            : 'text-n-slate-9 hover:bg-n-alpha-1 hover:text-n-slate-12'
        "
        @click="switchTab(tab.key)"
      >
        <span :class="tab.icon" class="size-3.5" />
        {{ tab.label }}
        <span
          v-if="tab.key === 'pending' && stats.pending > 0"
          class="ml-0.5 px-1.5 py-0 text-[10px] font-bold rounded-full"
          :class="
            activeTab === 'pending'
              ? 'bg-white/30 text-white'
              : 'bg-amber-500 text-white'
          "
        >
          {{ stats.pending }}
        </span>
      </button>
    </div>

    <!-- ── Filters ────────────────────────────────────────────────────────────── -->
    <div class="px-4 py-3 border-b border-n-weak shrink-0">
      <div class="flex flex-wrap items-center gap-2">
        <!-- Search -->
        <div class="relative flex-1 min-w-[200px]">
          <span
            class="absolute left-3 top-1/2 -translate-y-1/2 i-lucide-search size-3.5 text-n-slate-6 pointer-events-none"
          />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search lead, phone, project…"
            class="w-full h-9 pl-9 pr-3 text-sm border border-n-weak rounded-lg bg-white dark:bg-n-solid-3 text-n-slate-12 outline-none focus:ring-2 focus:ring-woot-500 transition-shadow"
            @input="load"
          />
        </div>

        <!-- Project dropdown -->
        <select
          v-model="filterProject"
          class="h-9 w-56 px-3 text-sm border border-n-weak rounded-lg bg-white dark:bg-n-solid-3 text-n-slate-11 outline-none focus:ring-2 focus:ring-woot-500 transition-shadow cursor-pointer"
          @change="load"
        >
          <option value="">All Projects</option>
          <option v-for="p in projects" :key="p.id" :value="p.id">
            {{ p.name }}
          </option>
        </select>

        <!-- Action type dropdown -->
        <select
          v-model="filterActionType"
          class="h-9 w-56 px-3 text-sm border border-n-weak rounded-lg bg-white dark:bg-n-solid-3 text-n-slate-11 outline-none focus:ring-2 focus:ring-woot-500 transition-shadow cursor-pointer"
          @change="load"
        >
          <option value="">All Action Types</option>
          <option
            v-for="o in ACTION_TYPE_OPTIONS"
            :key="o.value"
            :value="o.value"
          >
            {{ o.label }}
          </option>
        </select>

        <!-- Clear filters -->
        <button
          v-if="hasActiveFilters"
          class="h-9 flex items-center gap-1.5 px-3 text-sm font-medium text-ruby-600 border border-ruby-200 rounded-lg hover:bg-ruby-50 transition-colors"
          @click="clearFilters"
        >
          <span class="i-lucide-x size-3.5" />
          Clear
        </button>

        <!-- Result count -->
        <span class="ml-auto text-xs text-n-slate-7 whitespace-nowrap">
          {{ filteredActions.length }}
          {{ filteredActions.length === 1 ? 'action' : 'actions' }}
        </span>
      </div>
    </div>

    <!-- ── Error ──────────────────────────────────────────────────────────────── -->
    <div
      v-if="loadError"
      class="mx-4 mt-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex items-center gap-2 shrink-0"
    >
      <span class="i-lucide-alert-circle size-3.5" />
      {{ loadError }}
      <button class="underline font-medium ml-1" @click="refresh">Retry</button>
    </div>

    <!-- ── Loading skeleton ───────────────────────────────────────────────────── -->
    <div
      v-if="isLoading && !actions.length"
      class="flex-1 flex items-center justify-center"
    >
      <div class="flex flex-col items-center gap-3 text-n-slate-7">
        <span class="i-lucide-loader-2 size-7 animate-spin text-woot-500" />
        <span class="text-sm">Loading approval queue…</span>
      </div>
    </div>

    <!-- ── Empty state ────────────────────────────────────────────────────────── -->
    <div
      v-else-if="!filteredActions.length && !isLoading"
      class="flex-1 flex flex-col items-center justify-center gap-6 px-4"
    >
      <div
        class="flex items-center justify-center w-16 h-16 rounded-2xl bg-woot-50 dark:bg-woot-500/10"
      >
        <span class="i-lucide-shield-check size-8 text-woot-500" />
      </div>
      <div class="text-center max-w-sm">
        <p class="text-lg font-semibold text-n-slate-12 mb-2">All caught up</p>
        <p class="text-sm text-n-slate-8 leading-relaxed">
          No AI actions are waiting for approval. Sensitive actions — price
          sheets, payment plans, legal documents, and unit reservations — will
          appear here before the AI sends them.
        </p>
      </div>
      <div class="flex items-center gap-3">
        <button
          class="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-n-weak rounded-lg text-n-slate-9 hover:border-woot-400 hover:text-woot-600 transition-colors"
          @click="refresh"
        >
          <span class="i-lucide-refresh-cw size-3.5" />
          Refresh
        </button>
        <button
          v-if="isAdmin"
          class="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-woot-500 text-white rounded-lg hover:bg-woot-600 transition-colors"
          @click="openSettings"
        >
          <span class="i-lucide-settings size-3.5" />
          NEXCEO Settings
        </button>
      </div>
    </div>

    <!-- ── Action cards ────────────────────────────────────────────────────────── -->
    <div v-else class="flex-1 overflow-auto p-4 space-y-3">
      <!-- Refreshing banner -->
      <div
        v-if="isLoading"
        class="flex items-center gap-2 px-3 py-2 rounded-lg bg-woot-50 border border-woot-200 text-xs text-woot-600"
      >
        <span class="i-lucide-loader-2 size-3.5 animate-spin" />
        Refreshing…
      </div>

      <div
        v-for="action in filteredActions"
        :key="action.id"
        class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden"
      >
        <!-- Card header: action type + status badge -->
        <div
          class="flex items-center justify-between gap-2 px-4 py-3 border-b border-n-weak"
          :class="
            action.status === 'pending' ? 'bg-amber-50 dark:bg-amber-500/5' : ''
          "
        >
          <div class="flex items-center gap-2 min-w-0">
            <span
              class="size-4 shrink-0"
              :class="
                action.status === 'pending'
                  ? 'i-lucide-shield-alert text-amber-500'
                  : 'i-lucide-shield-check text-n-slate-6'
              "
            />
            <span class="text-sm font-semibold text-n-slate-12 truncate">
              {{ actionLabel(action.action_type) }}
            </span>
          </div>
          <span
            class="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full capitalize shrink-0"
            :class="statusMeta(action.status).cls"
          >
            <span
              class="w-1.5 h-1.5 rounded-full"
              :class="statusMeta(action.status).dot"
            />
            {{ action.status }}
          </span>
        </div>

        <!-- Card body -->
        <div class="px-4 py-3 space-y-3">
          <!-- Lead + Project + AI + Time row -->
          <div class="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-n-slate-8">
            <span v-if="action.lead" class="flex items-center gap-1">
              <span class="i-lucide-user size-3 shrink-0" />
              <span class="font-medium text-n-slate-11">
                {{
                  action.lead.name ||
                  action.lead.phone_number ||
                  `#${action.lead_id}`
                }}
              </span>
              <span
                v-if="action.lead.phone_number && action.lead.name"
                class="text-n-slate-7"
              >
                · {{ action.lead.phone_number }}
              </span>
            </span>

            <span v-if="action.project" class="flex items-center gap-1">
              <span class="i-lucide-building-2 size-3 shrink-0" />
              {{ action.project.name }}
            </span>

            <span v-if="action.ai_employee" class="flex items-center gap-1">
              <span class="i-lucide-bot size-3 shrink-0" />
              {{ action.ai_employee.name }}
            </span>

            <span
              class="flex items-center gap-1"
              :title="fmtDate(action.created_at)"
            >
              <span class="i-lucide-clock size-3 shrink-0" />
              {{ timeAgo(action.created_at) }}
            </span>

            <span
              v-if="action.expires_at && action.status === 'pending'"
              class="flex items-center gap-1 text-amber-600 font-medium"
            >
              <span class="i-lucide-timer size-3 shrink-0" />
              Expires {{ fmtDate(action.expires_at) }}
            </span>
          </div>

          <!-- Reason -->
          <div
            v-if="action.reason"
            class="text-xs text-n-slate-9 bg-n-alpha-1 px-3 py-2 rounded-lg"
          >
            <span class="font-medium text-n-slate-10">Reason: </span
            >{{ action.reason }}
          </div>

          <!-- Document preview -->
          <div
            v-if="action.document"
            class="flex items-center gap-3 px-3 py-2 rounded-lg border border-n-weak bg-n-background text-sm"
          >
            <span class="i-lucide-file-text size-4 text-woot-500 shrink-0" />
            <div class="min-w-0 flex-1">
              <p class="font-medium text-n-slate-11 truncate text-xs">
                {{
                  action.document.document_title ||
                  action.document.document_type
                }}
              </p>
              <a
                v-if="action.document.document_url"
                :href="action.document.document_url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-xs text-woot-500 hover:underline truncate block"
              >
                {{ action.document.document_url }}
              </a>
            </div>
          </div>

          <!-- Approval/rejection info -->
          <p
            v-if="action.approved_by"
            class="text-xs text-green-700 flex items-center gap-1"
          >
            <span class="i-lucide-check-circle size-3" />
            Approved by <strong>{{ action.approved_by.name }}</strong> ·
            {{ fmtDate(action.approved_at) }}
          </p>
          <p
            v-if="action.rejected_by"
            class="text-xs text-red-600 flex items-center gap-1"
          >
            <span class="i-lucide-x-circle size-3" />
            Rejected by <strong>{{ action.rejected_by.name }}</strong> ·
            {{ fmtDate(action.rejected_at) }}
            <span v-if="action.error_message" class="text-n-slate-7">
              — {{ action.error_message }}</span
            >
          </p>

          <!-- Error -->
          <p
            v-if="action.error_message && action.status === 'failed'"
            class="text-xs text-red-600 flex items-center gap-1"
          >
            <span class="i-lucide-alert-circle size-3" />
            Error: {{ action.error_message }}
          </p>

          <!-- ── Inline approve confirmation ───────────────────────────────── -->
          <div
            v-if="confirmingId === action.id"
            class="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm"
          >
            <span
              class="i-lucide-shield-alert size-4 text-amber-600 shrink-0"
            />
            <span class="flex-1 text-amber-800 font-medium"
              >Approve and execute this AI action?</span
            >
            <button
              class="px-3 py-1.5 text-xs font-semibold rounded-lg bg-woot-500 text-white hover:bg-woot-600 transition-colors"
              @click="confirmApprove(action)"
            >
              Yes, Approve
            </button>
            <button
              class="px-3 py-1.5 text-xs font-semibold rounded-lg border border-n-weak text-n-slate-8 hover:bg-n-alpha-1 transition-colors"
              @click="cancelApprove"
            >
              Cancel
            </button>
          </div>

          <!-- ── Actions row ─────────────────────────────────────────────────── -->
          <div class="flex flex-wrap items-center gap-2 pt-1">
            <!-- Approve / Reject (pending only) -->
            <template
              v-if="action.status === 'pending' && confirmingId !== action.id"
            >
              <button
                class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-woot-500 text-white hover:bg-woot-600 transition-colors"
                @click="startApprove(action)"
              >
                <span class="i-lucide-check size-3.5" />
                Approve & Execute
              </button>
              <button
                class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-red-300 text-red-600 hover:bg-red-50 transition-colors"
                @click="startReject(action)"
              >
                <span class="i-lucide-x size-3.5" />
                Reject
              </button>
            </template>

            <!-- Secondary links -->
            <a
              v-if="conversationUrl(action)"
              :href="conversationUrl(action)"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-1.5 px-3 py-1.5 text-xs text-n-slate-8 border border-n-weak rounded-lg hover:border-woot-400 hover:text-woot-600 transition-colors"
            >
              <span class="i-lucide-message-square size-3.5" />
              Conversation
              <span
                v-if="action.conversation_display_id"
                class="text-n-slate-6"
              >
                #{{ action.conversation_display_id }}
              </span>
            </a>

            <button
              v-if="action.lead_id"
              class="flex items-center gap-1.5 px-3 py-1.5 text-xs text-n-slate-8 border border-n-weak rounded-lg hover:border-woot-400 hover:text-woot-600 transition-colors"
              @click="openLeadEdit(action)"
            >
              <span class="i-lucide-user size-3.5" />
              View Lead
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Reject modal ────────────────────────────────────────────────────────── -->
    <teleport to="body">
      <div
        v-if="rejectingAction"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        @click.self="cancelReject"
      >
        <div
          class="bg-white dark:bg-n-solid-2 rounded-xl shadow-xl w-full max-w-md p-6 space-y-4"
        >
          <div class="flex items-center gap-3">
            <span class="i-lucide-x-circle size-5 text-red-500 shrink-0" />
            <h2 class="text-base font-semibold text-n-slate-12">
              Reject AI Action
            </h2>
          </div>
          <p class="text-sm text-n-slate-9">
            You are rejecting:
            <strong>{{ actionLabel(rejectingAction.action_type) }}</strong>
            <span v-if="rejectingAction.lead?.name">
              for {{ rejectingAction.lead.name }}</span
            >.
          </p>
          <div>
            <label class="block text-xs font-medium text-n-slate-8 mb-1.5">
              Reason <span class="text-n-slate-6">(optional)</span>
            </label>
            <textarea
              v-model="rejectReason"
              rows="3"
              placeholder="Why is this action being rejected?"
              class="w-full text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500 resize-none"
            />
          </div>
          <div class="flex gap-3">
            <button
              class="flex-1 py-2 text-sm font-medium rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
              @click="confirmReject"
            >
              Confirm Reject
            </button>
            <button
              class="flex-1 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-9 hover:bg-n-alpha-1 transition-colors"
              @click="cancelReject"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>
